import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wallet, Search, Download, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

type Row = {
  id: string;
  user_id: string;
  amount_etb: number;
  balance_after: number;
  reference_id: string | null;
  note: string | null;
  created_at: string;
  buyer_name?: string | null;
  buyer_email?: string | null;
  package_name?: string | null;
  package_sku?: string | null;
  invoice_number?: string | null;
  invoice_id?: string | null;
};

export default function CreditPurchasesLog() {
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['accounting-credit-purchases'],
    queryFn: async () => {
      const { data: txs, error } = await supabase
        .from('wallet_transactions' as any)
        .select('id, user_id, amount_etb, balance_after, reference_id, note, created_at')
        .eq('kind', 'credit_purchase')
        .eq('direction', 'debit')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      const rows = (txs as any[]) || [];
      const userIds = [...new Set(rows.map((r) => r.user_id).filter(Boolean))];
      const pkgIds = [...new Set(rows.map((r) => r.reference_id).filter(Boolean))];

      const [profilesRes, pkgsRes, invoicesRes] = await Promise.all([
        userIds.length
          ? supabase.from('profiles').select('user_id, full_name, email').in('user_id', userIds)
          : Promise.resolve({ data: [] as any[] }),
        pkgIds.length
          ? supabase.from('advertising_packages' as any).select('id, name, sku').in('id', pkgIds)
          : Promise.resolve({ data: [] as any[] }),
        userIds.length
          ? supabase
              .from('payment_invoices' as any)
              .select('id, invoice_number, user_id, package_id, amount_etb, paid_at, provider')
              .in('user_id', userIds)
              .eq('provider', 'wallet')
              .eq('status', 'paid')
              .order('paid_at', { ascending: false })
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const pMap = new Map<string, any>();
      ((profilesRes as any).data || []).forEach((p: any) => pMap.set(p.user_id, p));
      const pkgMap = new Map<string, any>();
      ((pkgsRes as any).data || []).forEach((p: any) => pkgMap.set(p.id, p));
      const invList = ((invoicesRes as any).data || []) as any[];

      return rows.map((r) => {
        const inv = invList.find(
          (i) =>
            i.user_id === r.user_id &&
            i.package_id === r.reference_id &&
            Number(i.amount_etb) === Number(r.amount_etb) &&
            Math.abs(new Date(i.paid_at).getTime() - new Date(r.created_at).getTime()) < 60_000,
        );
        const profile = pMap.get(r.user_id);
        const pkg = pkgMap.get(r.reference_id);
        return {
          ...r,
          buyer_name: profile?.full_name,
          buyer_email: profile?.email,
          package_name: pkg?.name,
          package_sku: pkg?.sku,
          invoice_number: inv?.invoice_number,
          invoice_id: inv?.id,
        } as Row;
      });
    },
  });

  const filtered = useMemo(() => {
    const rows = data || [];
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      [r.buyer_name, r.buyer_email, r.package_name, r.package_sku, r.invoice_number, r.note, r.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [data, q]);

  const total = filtered.reduce((s, r) => s + Number(r.amount_etb || 0), 0);

  const exportCsv = () => {
    const header = ['Date', 'Buyer', 'Email', 'Package', 'SKU', 'Amount ETB', 'Balance After', 'Invoice', 'Transaction ID'];
    const lines = filtered.map((r) => [
      format(new Date(r.created_at), 'yyyy-MM-dd HH:mm'),
      r.buyer_name || '',
      r.buyer_email || '',
      r.package_name || '',
      r.package_sku || '',
      r.amount_etb,
      r.balance_after,
      r.invoice_number || '',
      r.id,
    ]);
    const csv = [header, ...lines].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-purchases-${format(new Date(), 'yyyyMMdd-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-primary" /> Credit Purchases (Wallet)
          </h1>
          <p className="text-sm text-muted-foreground">
            All credit pack purchases paid from Agency / Developer wallet balances.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1" onClick={exportCsv} disabled={!filtered.length}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Purchases shown</p>
          <p className="text-2xl font-bold">{filtered.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total deducted (ETB)</p>
          <p className="text-2xl font-bold tabular-nums">{total.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Unique buyers</p>
          <p className="text-2xl font-bold">{new Set(filtered.map((r) => r.user_id)).size}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base">Transactions</CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search buyer, package, invoice…"
                className="pl-8 h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-64 m-4" />
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">No credit purchases yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Buyer</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Wallet After</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Transaction Ref</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {format(new Date(r.created_at), 'd MMM yyyy, HH:mm')}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{r.buyer_name || '—'}</div>
                        <div className="text-muted-foreground">{r.buyer_email || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{r.package_name || r.note || '—'}</div>
                        {r.package_sku && (
                          <Badge variant="outline" className="text-[10px] mt-0.5">{r.package_sku}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-destructive tabular-nums">
                        −{Number(r.amount_etb).toLocaleString()} ETB
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                        {Number(r.balance_after).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.invoice_number ? (
                          <Link to={`/dashboard/wallet/receipt/${r.invoice_id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                            <Receipt className="h-3 w-3" /> {r.invoice_number}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground" title={r.id}>
                        {r.id.slice(0, 8)}…
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
